import { useCallback, useState } from 'react';

export enum BUTTON_TEXT {
  SHOW_ALL = 'CX_SHOW_ALL',
  SHOW_LESS = 'CX_SHOW_LESS',
  SHOW_MORE = 'CX_SHOW_MORE',
}

interface ShowMoreProps {
  allBetsNumber: number;
  betIncrementNumber?: number;
  containerRef: React.MutableRefObject<HTMLElement | null>;
  initialVisibleBetsNumber: number;
  scrollTopNumber: number;
}

interface ShowMoreResponse {
  setVisibleBetsNumber: () => void;
  showMoreButtonText: string;
  visibleBetsNumber: number;
}

export const useShowMore = ({
  allBetsNumber,
  betIncrementNumber = 6,
  containerRef,
  initialVisibleBetsNumber,
  scrollTopNumber,
}: ShowMoreProps): ShowMoreResponse => {
  const [showMoreButtonText, setShowMoreButtonText] = useState(BUTTON_TEXT.SHOW_MORE);
  const [visibleBetsNumber, setVisibleBetsNumber] = useState(initialVisibleBetsNumber);

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      const { top } = containerRef.current.getBoundingClientRect();
      window.scrollTo({
        behavior: 'smooth',
        top: top + window.scrollY - scrollTopNumber,
      });
    }
  }, [containerRef, scrollTopNumber]);

  const clickHandler = useCallback(() => {
    const isShowAll = showMoreButtonText === BUTTON_TEXT.SHOW_ALL;
    const isShowLess = showMoreButtonText === BUTTON_TEXT.SHOW_LESS;
    const isNotEnoughBetsToIncrementSoShowAll = visibleBetsNumber + betIncrementNumber >= allBetsNumber;

    switch (true) {
      case isShowLess:
        setVisibleBetsNumber(initialVisibleBetsNumber);
        setShowMoreButtonText(BUTTON_TEXT.SHOW_MORE);
        scrollToTop();
        break;
      case isShowAll || isNotEnoughBetsToIncrementSoShowAll:
        setVisibleBetsNumber(allBetsNumber);
        setShowMoreButtonText(BUTTON_TEXT.SHOW_LESS);
        break;

      default:
        setVisibleBetsNumber(prev => prev + betIncrementNumber);
        setShowMoreButtonText(BUTTON_TEXT.SHOW_ALL);
        break;
    }
  }, [allBetsNumber, betIncrementNumber, initialVisibleBetsNumber, scrollToTop, showMoreButtonText, visibleBetsNumber]);

  return {
    setVisibleBetsNumber: clickHandler,
    showMoreButtonText,
    visibleBetsNumber,
  };
};
